import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import {
  Page,
  TextButton,
  useWithAppState,
} from "@towersystems/roam-common-native";
import { useEffect } from "react";
import ProductModel from "../../utilities/data/model/Product";
import { useOfflineProductSearch } from "../../utilities/product";
import { useProductListScreen } from "./hooks";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { useTranslation } from "../../utilities/i18n";
import { useLoadMoreList } from "../../utilities/load-more-list";
import { QUERY_PRODUCT_LIST } from "../../modules/products/utils";
import { ROAM_LOCATION_HEADER_KEY } from "../../constants";
import { SearchContext, useSearch } from "../../utilities/search";
import { FlatListSearchableSkeleton } from "../FlatListSearchableSkeleton";
import { useHasTransitionEnded } from "../../utilities/use-after-interactions";
import { StackScreenProps } from "@react-navigation/stack";
import React from "react";
import { useScheduler } from "../../utilities/scheduler";
import { productFilterValuesToApiInput } from "../../modules/products/common";
import { useListFilterContext } from "../../utilities/list-filter/use-list-filter-context";
import { ProductsFilterValues } from "../../modules/products/types";
import { ProductResourceList } from "../../modules/products/components/ProductResourceList/ProductResourceList";

export type ProductListScreenProps = StackScreenProps<
  any,
  "ProductListScreen"
> & {};

function ProductListScreenInner({
  onSelect,
  navigation,
}: ProductListScreenProps & {
  onSelect: (product: Product | ProductModel) => void;
}) {
  const online = useOnline();
  const { t } = useTranslation("product");
  const { appState } = useWithAppState();
  const search = useSearch();
  const { query, setQuery } = search;
  const { schedule } = useScheduler(500);
  const { filterState, watch } = useListFilterContext<ProductsFilterValues>();

  const watchedFilterValues = watch();

  const transitionEnded = useHasTransitionEnded(navigation);

  const loadMoreList = useLoadMoreList<Product>({
    initialLoad: false,
    query: QUERY_PRODUCT_LIST,
    resultKey: "products",
    sort: { updatedAt: "DESC" },
    context: {
      headers: {
        [ROAM_LOCATION_HEADER_KEY]: appState.locationId ?? undefined,
      },
    },
    variableFormatter: (variables) => {
      return {
        options: {
          ...variables,
          filter: {
            ...variables.filter,
          },
        },
      };
    },
  });

  React.useEffect(() => {
    if (loadMoreList.called) {
      schedule(() => handleLoadMore(true));
    }
  }, [query]);

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          term: { contains: query },
          ...productFilterValuesToApiInput(watchedFilterValues),
        },
      },
      reset
    );
  };

  useFocusEffect(
    React.useCallback(() => {
      navigation.setOptions({
        headerTitle() {
          return (
            <TextButton
              onPress={() => {
                navigation.navigate("ProductListFilterRootScreen", {
                  screen: "ProductListFilterScreen",
                });
              }}
            >
              {t("common:filter", {
                count: filterState.activeFiltersCount,
              })}
            </TextButton>
          );
        },
      });
    }, [filterState.activeFiltersCount])
  );

  useFocusEffect(
    React.useCallback(() => {
      handleLoadMore(true);
    }, [watchedFilterValues])
  );

  const {
    data: offlineItems,
    loading: offlineLoading,
    searchProducts,
  } = useOfflineProductSearch();

  useEffect(() => {
    if (!online) {
      searchProducts(query);
    }
  }, [online, query]);

  const normalizedData = online
    ? {
        items: loadMoreList.items.map((item) => item),
        loading: loadMoreList.loading,
      }
    : { items: [], loading: false };

  const items = normalizedData.items ?? [];

  if (
    (loadMoreList.loading && loadMoreList.items.length == 0 && !query.length) ||
    false == transitionEnded
  ) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />
      </Page>
    );
  }

  return (
    <SearchContext.Provider value={search}>
      <Page scrollable={false} horizontalPadding={0} fullWidth>
        <ProductResourceList
          onItemPress={(item) => onSelect(item)}
          items={items as Product[]}
          onEndReached={() => handleLoadMore(false)}
        />
      </Page>
    </SearchContext.Provider>
  );
}

export const ProductListScreen = (props: ProductListScreenProps) => {
  const navigation = useNavigation();
  const { onSelect } = useProductListScreen();

  function handleSelect(product: Product | ProductModel) {
    onSelect(product);
    navigation.goBack();
  }

  return <ProductListScreenInner {...props} onSelect={handleSelect} />;
};
